import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut, ChevronDown, Bell } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAppNavbarHidden } from '../contexts/AppNavbarVisibilityContext';
import { BravosWordmark } from './BravosWordmark';
import { accountNavDisplayName } from '../pages/account/accountShared';

export const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hiddenByPage = useAppNavbarHidden();

  // Routes with no fixed pathname (the catch-all 404) opt out via useHideAppNavbar
  if (hiddenByPage) {
    return null;
  }

  // Landing uses its own floating nav over the hero
  if (location.pathname === '/' && !user) {
    return null;
  }

  // Hide navbar on auth (signup / email confirmation), trainer dashboard, member detail, member dashboard, account page, chat, workout plan detail page, and trainer workout detail page (all use custom headers / their own primary nav)
  if (location.pathname === '/auth' ||
      location.pathname === '/privacy' || location.pathname === '/terms' ||
      location.pathname.startsWith('/invite/') ||
      location.pathname === '/trainer-dashboard' ||
      location.pathname.startsWith('/trainer-dashboard/member/') ||
      location.pathname.startsWith('/trainer-dashboard/workout/') ||
      location.pathname === '/member-dashboard' ||
      location.pathname.startsWith('/account') ||
      location.pathname === '/chat' ||
      location.pathname === '/loading-preview' ||
      location.pathname.startsWith('/trainer-dashboard/workout-plan/')) {
    return null;
  }

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Personal Trainers', href: '/personal-trainers' },
    ...(profile?.role !== 'trainer'
      ? [{ name: 'Exercise Library', href: '/exercises' }]
      : []),
    { name: 'Workouts', href: '/workouts' },
    { name: 'Workout Plans', href: '/workout-plans' },
  ];

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const getDashboardLink = () => {
    if (!profile) return '/auth';
    return profile.role === 'trainer' ? '/trainer-dashboard' : '/member-dashboard';
  };

  return (
    <nav className="hidden md:block bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and main navigation */}
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 hidden md:block" aria-label="Bravos home">
              <BravosWordmark className="h-[1.5rem] w-auto" />
            </Link>
            
            {/* Desktop navigation */}
            <div className="hidden md:ml-10 md:flex md:space-x-8 md:items-center">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-gray-700 hover:text-primary-600 px-3 py-2 text-sm font-medium transition-colors"
                >
                  {item.name}
                </Link>
              ))}
              {user && profile?.role === 'member' && (
                <Link
                  to="/notifications"
                  className="text-gray-700 hover:text-primary-600 px-3 py-2 text-sm font-medium transition-colors flex items-center gap-1"
                >
                  <Bell className="w-4 h-4" />
                  Notifications
                </Link>
              )}
            </div>
          </div>

          {/* Right side - Auth buttons or user menu */}
          <div className="flex items-center">
            {user ? (
              <div className="relative hidden md:block">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 text-gray-700 hover:text-primary-600 transition-colors"
                >
                  <User className="w-5 h-5" />
                  <span className="text-sm font-medium">
                    {accountNavDisplayName(profile, user)}
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>

                {/* User dropdown menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                    <Link
                      to={getDashboardLink()}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <LogOut className="w-4 h-4 inline mr-2" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/auth"
                className="hidden md:inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-[var(--ink)] bg-[var(--action-primary)] hover:bg-[var(--action-primary-pressed)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                Get Started
              </Link>
            )}

            {/* Mobile menu button - hidden on mobile */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="hidden ml-4 p-2 rounded-md text-gray-700 hover:text-primary-600 hover:bg-gray-100 transition-colors"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile navigation */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-gray-200">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="text-gray-700 hover:text-primary-600 block px-3 py-2 text-base font-medium transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            {user && profile?.role === 'member' && (
              <Link
                to="/notifications"
                className="text-gray-700 hover:text-primary-600 block px-3 py-2 text-base font-medium transition-colors flex items-center gap-2"
                onClick={() => setIsMenuOpen(false)}
              >
                <Bell className="w-4 h-4" />
                Notifications
              </Link>
            )}
            {user && (
              <>
                <Link
                  to={getDashboardLink()}
                  className="text-gray-700 hover:text-primary-600 block px-3 py-2 text-base font-medium transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleSignOut}
                  className="text-gray-700 hover:text-primary-600 block w-full text-left px-3 py-2 text-base font-medium transition-colors"
                >
                  Sign Out
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}; 